import React, { useEffect, useLayoutEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useParams, useLocation } from 'react-router-dom'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect } from 'react-router'

import {
  applyFilters,
  setSavedFilterById,
  clearSavedFilter,
  loadInitialData,
} from '@/reducers/analytics/analytics.actions'
import { ANALYTICS_FILTER_TYPE } from '@/reducers/savedFilters/savedFilters.constants'

import { SavedFilters } from '@/components/savedFilters/SavedFilters'
import { Menu } from '@/components/navigation/Menu'
import { PageHeader } from '@/components/headers/PageHeader'
import { AnalyticsFilters } from './AnalyticsFilters'
import { OverviewSection } from './sections/OverviewSection'
import { DynamicPromptSection } from './sections/DynamicPromptSection'
import { ChecklistSection } from './sections/ChecklistSection'
import { NotificationsSection } from './sections/NotificationsSection'
import { PostCallSection } from './sections/PostCallSection'
import { UsageSection } from './sections/UsageSection'
import { ExportCSVSection } from './sections/ExportCSVSection'
import { ANALYTICS_SECTIONS, ROUTE_TO_SECTION } from './analytics.constants'

import './AnalyticsPage.scss'

const { OVERVIEW, CHECKLIST, DYNAMIC_PROMPT, NOTIFICATIONS, POSTCALL, USAGE, CSV } =
  ANALYTICS_SECTIONS

const sectionData = [
  {
    section: OVERVIEW,
    title: 'Overview',
    pageTitle: 'Reporting Overview',
    component: OverviewSection,
  },
  {
    section: CHECKLIST,
    title: 'Checklist',
    pageTitle: 'Checklist Reporting',
    component: ChecklistSection,
  },
  {
    section: DYNAMIC_PROMPT,
    route: 'dynamic-prompt',
    title: 'Dynamic Prompt',
    pageTitle: 'Dynamic Prompt Reporting',
    component: DynamicPromptSection,
  },
  {
    section: NOTIFICATIONS,
    title: 'Notifications',
    pageTitle: 'Notifications Reporting',
    component: NotificationsSection,
  },
  {
    section: POSTCALL,
    title: 'Post Call',
    pageTitle: 'Post Call Reporting',
    component: PostCallSection,
  },
  { section: USAGE, title: 'Usage', pageTitle: 'Usage Reporting', component: UsageSection },
  { section: CSV, title: 'Export CSV', pageTitle: 'Export CSV', component: ExportCSVSection },
]

// The Overview tab lives at the bare /reports path, so a prefix match would
// keep it active on every section.
const isOverviewActive = (match, location) => location.pathname === '/reports'

// Notifications stays reachable by URL only, matching the old sidebar; the
// CSV export keeps its own Import/Export sidebar link instead of a tab.
const sectionTabs = sectionData
  .filter(({ section }) => section !== NOTIFICATIONS && section !== CSV)
  .map(({ section, route, title }) => ({
    accessor: section,
    label: title,
    to: section === OVERVIEW ? '/reports' : `/reports/${route || section}`,
    isActive: section === OVERVIEW ? isOverviewActive : undefined,
    dataTestId: `analytics-tab-${section}`,
  }))

function getPageTitle(currentSection) {
  return (
    sectionData.find((data) => data.section === currentSection)?.pageTitle || 'Reporting Overview'
  )
}

function renderSection(currentSection) {
  const Component =
    sectionData.find((data) => data.section === currentSection)?.component || OverviewSection

  return <Component />
}

export const AnalyticsPage = () => {
  const dispatch = useDispatch()
  const { section } = useParams()
  const currentSection = ROUTE_TO_SECTION[section] || section || OVERVIEW
  const location = useLocation()
  const { customBranding } = useSelector((state) => state.customBranding)
  const {
    initialLoadComplete,
    data: { selectedSavedFilterId, fetchedAgentsOrg, fetchedTagsOrg, fetchedPlaybooksOrg },
    selectedHierarchyOrganization,
  } = useSelector((state) => state.analytics)
  const {
    organizationid: currentUserOrgId,
    own_organization_id: ownOrganizationId,
    hierarchy_manager: isHierarchyManager,
  } = useSelector((state) => state.currentUser)
  const shouldShowFilters = () => {
    if (currentSection === CSV) {
      return false
    }
    if (isHierarchyManager && selectedHierarchyOrganization !== ownOrganizationId) {
      return false
    }

    return true
  }
  const showFilters = shouldShowFilters()
  const siteTitle = customBranding?.customBrandingDisplayName || 'Balto Cloud'

  const handleSelectSavedFilter = (value) => {
    if (!value) {
      dispatch(clearSavedFilter())
    } else {
      dispatch(setSavedFilterById(value, true, currentSection))
    }
  }

  useEffect(() => {
    // Only run loadInitialData once on mount, then re-fetch section data on route changes
    if (!initialLoadComplete) {
      dispatch(loadInitialData())
    } else {
      // All data is loaded or we've changed routes. Re-fetch all the data
      dispatch(applyFilters(currentSection))
    }
  }, [initialLoadComplete, currentSection])

  useEffect(() => {
    const allMatch = [
      currentUserOrgId,
      fetchedAgentsOrg,
      fetchedTagsOrg,
      fetchedPlaybooksOrg,
    ].every((orgId) => orgId === selectedHierarchyOrganization)

    if (!allMatch) {
      dispatch(loadInitialData())
    }
  }, [selectedHierarchyOrganization])

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  // reroute legacy deck URL to new dynamic prompt URL
  if (section === 'deck') {
    return <Redirect to="/reports/dynamic-prompt" />
  }

  return (
    <>
      <Helmet>
        <title>{`${getPageTitle(currentSection)} | ${siteTitle}`}</title>
      </Helmet>

      <PageHeader
        title={getPageTitle(currentSection)}
        data-testid="analytics-page"
        actions={
          showFilters && (
            <SavedFilters
              filterType={ANALYTICS_FILTER_TYPE}
              selectedSavedFilterId={selectedSavedFilterId}
              handleSelectSavedFilter={handleSelectSavedFilter}
              filterFormOptions={[
                'name',
                'isDefault',
                'filterPropertiesHeader',
                'dateRange',
                'agents',
                'tags',
                'playbooks',
                'callDuration',
                'maxCallDuration',
                'includeManagers',
                'voipCampaigns',
              ]}
            />
          )
        }
      />
      {currentSection !== CSV && (
        <Menu menuStyle="bar" items={sectionTabs} data-testid="analytics-tabs" />
      )}
      <AnalyticsFilters />

      {renderSection(currentSection)}
    </>
  )
}

export default withLDConsumer()(AnalyticsPage)
